import React from 'react';

/**
 * PageHeader Component
 *
 * @param {Object} props
 * @param {React.ReactNode} props.title - Page title
 * @param {React.ReactNode} [props.description] - 부제/보조 정보 (문자·뱃지 등)
 * @param {React.ReactNode} props.actions - Action buttons (optional)
 * @param {React.ReactNode} props.rightContent - Content to display on the right
 */
interface PageHeaderProps {
    title?: React.ReactNode;
    description?: React.ReactNode;
    /** @deprecated 미사용 (breadcrumb 삭제됨). 호환성 위해 수용만 함 */
    breadcrumbs?: { label: string; link?: string }[];
    actions?: React.ReactNode;
    rightContent?: React.ReactNode;
    align?: 'left' | 'center';
    withBackground?: boolean;
    /** 관리자 페이지 등에서 타이틀 위치·사이즈 통일용. default: 3xl bold, admin: 2xl semibold */
    titleSize?: 'default' | 'admin';
    className?: string;
    style?: React.CSSProperties;
}

const TITLE_CLASS = {
    default: 'font-bold text-gray-900 text-3xl',
    admin: 'font-bold text-gray-900 text-3xl',
} as const;

export default function PageHeader({
    title,
    description,
    breadcrumbs: _breadcrumbs,
    actions,
    rightContent,
    align = 'left',
    withBackground = false,
    titleSize = 'default',
    className = '',
    style = {}
}: PageHeaderProps) {
    const isCentered = align === 'center';

    // Background style classes
    const backgroundClasses = withBackground
        ? '-mx-4 bg-white border-b border-gray-100 mb-8 py-10 px-4'
        : 'py-8';

    return (
        <div className={`w-full ${className}`} style={style}>
            <div className={`${backgroundClasses} ${isCentered ? 'flex flex-col items-center text-center' : ''}`}>
                <div className={`flex gap-4 w-full ${isCentered ? 'flex-col items-center justify-center' : 'flex-row flex-nowrap items-center'}`}>
                    {title && (
                        <div className={`flex items-center gap-4 min-w-0 flex-1 ${isCentered ? 'justify-center' : 'min-w-[8rem]'}`}>
                            <h1 className={`${TITLE_CLASS[titleSize]} truncate`}>
                                {title}
                            </h1>
                        </div>
                    )}

                    {!isCentered && (actions || rightContent) && (
                        <div className="flex items-center gap-4 flex-shrink-0 ms-auto">
                            {actions && (
                                <div className="flex items-center gap-2">
                                    {actions}
                                </div>
                            )}
                            {rightContent && (
                                <div className="flex-shrink-0">
                                    {rightContent}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {description != null && description !== '' && (
                    <div
                        className={`mt-2 text-gray-600 max-w-3xl flex flex-wrap items-center gap-x-3 gap-y-2 ${isCentered ? 'mx-auto justify-center' : ''}`}
                    >
                        {description}
                    </div>
                )}

                {isCentered && (actions || rightContent) && (
                    <div className="flex items-center gap-4 mt-6">
                        {actions}
                        {rightContent}
                    </div>
                )}
            </div>
        </div>
    );
}
